import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AuthContext, type Session } from "./authContext.ts";
import { refrescarSesion } from "../lib/api.ts";

const STORAGE_KEY = "argus.session.v1";
const MS_ANTES_DE_EXPIRAR = 120_000;
const MS_REINTENTO_MINIMO = 10_000;

/**
 * Persistencia consciente de sesión:
 * - Web usa sessionStorage, no localStorage: sobrevive refresh/back-forward pero
 *   se borra al cerrar la pestaña y evita un refresh token durable en disco.
 * - En Capacitor/WebView esto debe migrar a secure storage nativo o cookie
 *   HttpOnly controlada por backend antes de shippear wrapper.
 */
function cargarSesion(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: unknown = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    const s = data as Partial<Session>;
    if (typeof s.userId !== "string" || typeof s.tenantId !== "string" || typeof s.accessToken !== "string") return null;
    if (s.rol !== "ventas" && s.rol !== "rec" && s.rol !== "admin" && s.rol !== "auditor") return null;
    if (s.aal !== "aal1" && s.aal !== "aal2" && s.aal !== "demo") return null;
    return s as Session;
  } catch {
    return null;
  }
}

function guardarSesion(session: Session | null) {
  if (typeof window === "undefined") return;
  if (!session) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/** Provee la sesión a la app con persistencia y refresh silencioso. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(() => cargarSesion());
  const [authError, setAuthError] = useState<string | null>(null);

  const setSession = useCallback((s: Session | null) => {
    setSessionState(s);
    guardarSesion(s);
    if (s) setAuthError(null);
  }, []);

  useEffect(() => {
    if (!session || session.aal === "demo" || !session.refreshToken || !session.expiresAt) return;
    const delay = Math.max(session.expiresAt - Date.now() - MS_ANTES_DE_EXPIRAR, MS_REINTENTO_MINIMO);
    const timer = window.setTimeout(() => {
      void refrescarSesion(session.refreshToken ?? "", session.email ?? session.nombre ?? "").then(
        (apiSession) => {
          const nextSession: Session = {
            userId: apiSession.userId,
            tenantId: apiSession.tenantId,
            rol: apiSession.rol,
            email: apiSession.email,
            nombre: session.nombre ?? apiSession.email,
            accessToken: apiSession.accessToken,
            aal: apiSession.aal,
          };
          const refreshToken = apiSession.refreshToken ?? session.refreshToken;
          if (refreshToken) nextSession.refreshToken = refreshToken;
          if (apiSession.expiresAt) nextSession.expiresAt = apiSession.expiresAt;
          setSession(nextSession);
        },
        (e: unknown) => {
          const detalle = e instanceof Error ? e.message : "No se pudo renovar la sesión.";
          setSessionState(null);
          guardarSesion(null);
          setAuthError(`Tu sesión expiró o no pudo renovarse: ${detalle}`);
        },
      );
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [session, setSession]);

  return (
    <AuthContext.Provider value={{ session, authError, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}
